/**
 * useSearchRerun (M2-C, 2026-09-16) — повторный поиск выбранных строк.
 *
 * - useRerunEstimate: POST /api/v1/search/rerun-estimate — смета перед запуском.
 *   Pure SELECT на бэкенде: без hold, без pipeline, без Celery.
 * - useRestartRows: POST /api/v1/pipeline/restart-rows — запуск rerun.
 *   onSuccess: setCurrentPipelineId(pr.id); инвалидация ['search','results',datasetId]
 *   (truth-результаты по строкам обновятся после завершения прогона) — в вызывающей стороне.
 *
 * Повторные запуски с тем же request_id возвращают 200 + тот же run
 * (идемпотентность бэкенда), поэтому request_id генерируется на каждую попытку.
 */
import { useMutation } from '@tanstack/react-query';

import api from '@/lib/api';
import { useSearchStore } from '@/store/searchStore';

export interface RerunEstimateRequest {
  dataset_id: string;
  row_ids: string[];
  tier_key: string;
  is_test_run: boolean;
  test_run_limit?: number;
  priority_domains?: string[];
}

/** Формат ответа POST /search/rerun-estimate (Decimal-поля — строки). */
export interface RerunEstimateResponse {
  currency: string;
  tier_key: string;
  row_count_total: number;
  row_count_effective: number;
  is_test_run: boolean;
  test_run_limit: number;
  max_queries_per_row: number;
  max_pages_per_row: number;
  max_candidates_per_query: number;
  max_queries_total: number;
  max_pages_total: number;
  max_extracts_total: number;
  estimate_min_credits: string;
  estimate_max_credits: string;
  per_row_min_credits: string;
  per_row_max_credits: string;
  breakdown: {
    per_row_queries_max: number;
    per_row_pages_max: number;
    per_row_candidates_max: number;
    unit_credit_per_query: string;
    unit_credit_per_page_fetch: string;
    unit_credit_per_extract: string;
    priority_domains_count: number;
    from_admin: boolean;
    notes: string;
  };
  warning?: string | null;
}

export interface RestartRowsRequest {
  dataset_id: string;
  row_ids: string[];
  tier_key: string;
  is_test_run: boolean;
  test_run_limit?: number;
  request_id: string;
  selected_columns?: string[];
  priority_domains?: string[];
}

/** Формат ответа POST /pipeline/restart-rows (PipelineRunRead). */
export interface PipelineRunRead {
  id: string;
  status: string;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  error_rows: number;
  is_test_run: boolean;
}

/** Смета rerun по выбранным строкам. Вызывается вручную (кнопка «Смета»). */
export function useRerunEstimate() {
  return useMutation<RerunEstimateResponse, Error, RerunEstimateRequest>({
    mutationFn: async (body) => {
      const { data } = await api.post<RerunEstimateResponse>(
        '/search/rerun-estimate',
        body,
      );
      return data;
    },
    retry: false,
  });
}

/**
 * Запуск rerun выбранных строк. onSuccess: текущий пайплайн переключается на
 * новый run (progress через существующий polling usePipelineStatus). Инвалидацию
 * truth-результатов и сброс выбора делает вызывающая сторона (ProcessSearchPage),
 * у которой есть queryClient.
 */
export function useRestartRows() {
  const setCurrentPipelineId = useSearchStore((s) => s.setCurrentPipelineId);
  return useMutation<PipelineRunRead, Error, RestartRowsRequest>({
    mutationFn: async (body) => {
      const { data } = await api.post<PipelineRunRead>(
        '/pipeline/restart-rows',
        body,
      );
      return data;
    },
    onSuccess: (pr) => {
      setCurrentPipelineId(pr.id);
    },
    retry: false,
  });
}